/**
 * Client API minimal pour le backend KTM Atelier Board.
 * - Ajoute automatiquement Authorization: Bearer <accessToken>
 * - Sur 401, tente un /auth/refresh une fois, puis rejoue la requête
 * - Si refresh échoue → reset auth store + redirige vers /login
 */
import { useAuthStore } from '@/stores/authStore';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export type ApiError = {
  status: number;
  error: string;
  message: string;
  details?: unknown;
};

class HttpError extends Error {
  constructor(public payload: ApiError) {
    super(payload.message);
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { user: AuthUser; accessToken: string };
      useAuthStore.getState().setSession(data.user, data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export type AuthUser = {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  role: 'admin' | 'reception' | 'atelier' | 'mecanicien';
  mecanicienId: string | null;
};

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  skipAuth?: boolean;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, skipAuth, ...rest } = options;
  const accessToken = useAuthStore.getState().accessToken;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((headers as Record<string, string>) ?? {}),
  };
  if (accessToken && !skipAuth) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (res.status === 401 && !skipAuth && accessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...options, skipAuth: true });
    }
    useAuthStore.getState().clearSession();
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    throw new HttpError({
      status: res.status,
      error: payload.error ?? 'UnknownError',
      message: payload.message ?? `HTTP ${res.status}`,
      details: payload.details,
    });
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

export { HttpError };
